import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";



// Utils
import { DataContext } from "../utils/DataContext";
import { UserAuth } from "../utils/AuthContext";
import { sanitizeNumber } from "../utils/sanitizeNumber";
import useRequest from "../utils/useRequest";

// Components
import Modal from "./Modal";
import Loader from "./Loader";
import TextDiv from "./TextDiv";
import CustomInput from "./CustomInput";
import CustomButton from "./CustomButton";
import ErrorBox from "./ErrorBox";
import Dropdown from "./Dropdown";
import { formatDate } from "../utils/toDate";
import CustomInputWithUnit from "./CustomInputWithUnit";
import axios from "axios";
import HotDropdown from "./HotDropdown";

const PaymentModal = ({
  title = "payment",
  selected,
  handleTogglePaymentModal,
  withSubCompany,
  modalSize,
  transactionType = "debtRepayment",
  refetchData,
  setShouldGetCustomers,
  isBank = false,
}) => {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const {
    subCompanies,
    getSubCompanies,
    selectedSubCompany,
    handleSelectSubCompany,
  } = useContext(DataContext);

  const [inputs, setInputs] = useState({
    amount: 0,
    newLeftAmount: selected?.totalLeft,
    notes: "",
    date: new Date(),
    bankName: "",
    bankId: null,
  });

  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState({
    id: null,
    name: t("choose"),
  });

  const { loading, success, error, setError, refetch } = useRequest(
    `/api/transaction/createTransaction`,
    "POST",
    {
      amount: inputs.amount,
      notes: inputs.notes,
      date: inputs.date,
      bankName: inputs.bankName,
      name: transactionType !== "debtRepayment" ? "addDebt" : null,
      transactionType,
      customerId: isBank ? "" : selected?.id,
      userId: userData?.userId,
      subCompanyId: selectedSubCompany?.id,
      bankId: isBank ? selected.id : selectedBank.id ? selectedBank.id : null,
    },
    null,
    false
  );

  useEffect(() => {
    if (success) {
      setError("");
      handleTogglePaymentModal();
      if (refetchData) {
        refetchData();
        setShouldGetCustomers?.(true);
      }
    }
  }, [success]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "amount") {
      setInputs((prev) => {
        const sanitizedValue = value;
        const sanitizedLeftAmount = selected?.totalLeft;
        const calculatedNewLeftAmount = sanitizedLeftAmount - sanitizedValue;
        return {
          ...prev,
          [name]: value,
          newLeftAmount: calculatedNewLeftAmount,
        };
      });
    } else if (name == "date") {
      setInputs((prev) => ({ ...prev, [name]: new Date(value) }));
    } else {
      setInputs((prev) => ({ ...prev, [name]: value }));
    }

    setError("");
  };

  const handleSave = () => {
    if (!inputs.amount) {
      setError("emptyInputField");
      return;
    }
    if (withSubCompany && !selectedSubCompany?.id) {
      setError("chooseASubCompany");
      return;
    }
    refetch();
  };

  const getAllBanks = async (page, limit, undefined, searchQuery) => {
    try {
      const banks = await axios.get(`${process.env.REACT_APP_URL}/api/bank`, {
        headers: {
          Authorization: `Bearer ${userData?.token}`,
          "organization-id": userData?.organizationId,
        },
        params: {
          page: page,
          limit: limit,
          searchQuery: searchQuery,
          isIraqi: false,
        },
      });

      if (banks.status === 200) {
        // return banks;
        setBanks(banks?.data?.banks);
      }
      return banks;
    } catch (error) {
      setError(error?.response?.message || "Failed");
      console.error("Failed to create the supplier", error);
    }
  };

  const handleSelectBank = (bank) => {
    if (bank) {
      setSelectedBank({ id: bank.id, name: bank.name });
      setInputs((prev) => ({ ...prev, bank: selectedBank }));
    } else {
      setSelectedBank(null);
      setInputs((prev) => ({ ...prev, bank: null }));
    }
    setError("");
  };

  return (
    <Modal
      title={t(title)}
      onClose={handleTogglePaymentModal}
      modalSize={modalSize}
      footer={
        <>
          {loading ? <Loader /> : null}
          <section className="edit-user-actions">
            <CustomButton
              title={t("confirm")}
              onClick={handleSave}
              icon='save'
              iconSize={16}
              disabled={loading}
            />

            <CustomButton
              title={t("cancel")}
              onClick={handleTogglePaymentModal}
              variant="blank"
              icon="close"
              iconSize={14}
            />
          </section>
        </>
      }
    >
      {error && <ErrorBox>{t(error)}</ErrorBox>}

      <section>
        {withSubCompany && (
          <div className="o-row o-row--vertical-align_center o-row--gap-size_300">
            <label className="u-min-width-specific_100">
              {t("subCompany")}:
            </label>
            <Dropdown
              options={[{ id: "", nameInAr: "All" }, ...subCompanies]}
              selected={selectedSubCompany}
              setSelected={(subCompany) =>
                handleSelectSubCompany(subCompany, "noRequest")
              }
              thick
              nameInAr={true}
              getData={getSubCompanies}
              errorText={error === "chooseASubCompany" ? error : null}
            />
          </div>
        )}
        <TextDiv
          title={isBank ? "bank" : "customer"}
          value={selected?.name}
          divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
        />
      </section>

      <div className="o-row o-row--vertical-align_center o-row--gap-size_300">
        <label htmlFor="dateInput" className="u-min-width-specific_100">
          {t("Date")}:
        </label>
        <CustomInput
          name="date"
          value={formatDate(inputs.date)}
          type="date"
          onChange={handleInputChange}
          errorText={error}
          flex
          id="dateInput"
        />
      </div>

      <section className="o-column o-column--gap-size_500">
        <div className="o-row o-row--vertical-align_center o-row--gap-size_300">
          <label htmlFor="amountInput" className="u-min-width-specific_100">
            {t("amount")}:
          </label>
          <CustomInputWithUnit
            name="amount"
            value={inputs.amount}
            type="moneyV2"
            onChange={handleInputChange}
            id="amountInput"
            errorText={error}
            flex
            unit={selected?.currency || "USD"}
          />
        </div>

        {transactionType === "debtRepayment" && !isBank && (
          <div className="o-row o-row--vertical-align_center o-row--gap-size_300">
            <label
              htmlFor="bankNameInput"
              className="u-min-width-specific_100 "
            >
              {t("bank")}:
            </label>
            {/* <CustomInput
              name="bankName"
              value={inputs.bankName}
              onChange={handleInputChange}
              type="text"
              flex
              id="bankNameInput"
            /> */}

            <HotDropdown
              initOptions={banks}
              setOptions={setBanks}
              selected={selectedBank}
              setSelected={(bank) => handleSelectBank(bank)}
              getData={getAllBanks}
              optionsName={"banks"}
              errorText={error}
              flex
            />
          </div>
        )}

        <div className="o-row o-row--vertical-align_start o-row--gap-size_300">
          <label htmlFor="notesInput" className="u-min-width-specific_100 ">
            {t("notes")}:
          </label>
          <CustomInput
            name="notes"
            value={inputs.notes}
            onChange={handleInputChange}
            id="notesInput"
            type="textarea"
            flex
          />
        </div>
      </section>
    </Modal>
  );
};

export default PaymentModal;
